import { Fieldset } from '@local/do-secundo-fieldset'
import { Button, ButtonType, ButtonVariant } from '@local/do-secundo-button'
import React, { useState, useEffect } from 'react'
import { CheckoutFormBodyProps } from './utils'
import { useRestaurant } from '@local/do-secundo-restaurant-provider/src'
import { getCheckTotal } from '../../utils/cart-helpers'
import { CartCheckoutFields } from './CartCheckoutFields'
import { AdyenPayment } from '@toasttab/do-secundo-adyen-payment'
import { CheckoutDetailsStep } from './CheckoutDetailsStep'
import { LegalCopy } from '../LegalCopy/LegalCopy'
import styles from './CheckoutForm.module.css'
import { CompletedCustomerInfo } from './CustomerInfo/CompletedCustomerInfo'
import { CheckoutError } from '../CheckoutError/CheckoutError'
import { t } from '@local/translations'

/**
 * Implements two-step checkout for international restaurants where payment is collected separately.
 * Manages navigation between details and payment steps, rendering Adyen in step 2.
 * Selected by CheckoutFormContent when isIntlRx is true, complementing DomesticCheckoutFormBody.
 */
export const InternationalCheckoutFormBody = (props: CheckoutFormBodyProps) => {
  const {
    customer,
    values,
    isSubmitting,
    canCheckout,
    tipAmount,
    handleSubmit,
    setFieldValue,
    checkoutProps,
    confirmOrderError
  } = props
  const { cart } = checkoutProps
  const { restaurantGuid, currency, locale, country } = useRestaurant()

  // Local state for two-step checkout flow
  const [checkoutStep, setCheckoutStep] = useState<'DETAILS' | 'PAYMENT'>(
    'DETAILS'
  )

  // Auto-submit when payment is complete
  useEffect(() => {
    if (values.paymentGuid && checkoutStep === 'PAYMENT') {
      handleSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.paymentGuid])

  // International payment step UI
  if (checkoutStep === 'PAYMENT') {
    return (
      <>
        <Fieldset
          label={t('international-checkout-form-body.your-information')}
        >
          <CompletedCustomerInfo
            customer={customer}
            companyName={values.companyName}
          />
        </Fieldset>
        <CartCheckoutFields
          values={values}
          customer={customer}
          cart={cart!}
          showTip={'read_only'}
          checkoutPaymentAmount={checkoutProps.checkoutPaymentAmount}
        />
        <Fieldset
          label={
            <Button onClick={() => setCheckoutStep('DETAILS')}>
              {t('international-checkout-form-body.go-back-to-edit')}
            </Button>
          }
        >
          {confirmOrderError ? (
            <div className='px-5 pb-0'>
              <CheckoutError
                loading={isSubmitting}
                error={confirmOrderError.error}
                cart={cart!}
              />
            </div>
          ) : (
            <AdyenPayment
              payment={{
                checkAmount: checkoutProps.checkoutPaymentAmount.totalAmountDue,
                tipAmount: tipAmount,
                emailAddress: customer.email
              }}
              config={{
                guid: restaurantGuid,
                currency: currency!!,
                locale: locale!!,
                country: country,
                channel: 'CATERING_OO'
              }}
              onPaymentCompleted={(paymentGuid) => {
                setFieldValue('paymentGuid', paymentGuid)
              }}
            />
          )}
        </Fieldset>
      </>
    )
  }

  const sharedButtonProps = {
    'data-testid': 'submit-button',
    id: 'submit-button',
    variant: ButtonVariant.PRIMARY,
    loading: isSubmitting,
    disabled: !canCheckout
  }

  // International details step UI
  return (
    <>
      <CheckoutDetailsStep {...props} />
      <div className={styles.wrapper}>
        {checkoutStep === 'DETAILS' && values.paymentType === 'CREDIT' ? (
          <Button
            {...sharedButtonProps}
            type={ButtonType.BUTTON}
            onClick={() => setCheckoutStep('PAYMENT')}
          >
            {t('international-checkout-form-body.continue-to-payment')}
          </Button>
        ) : (
          <Button {...sharedButtonProps} type={ButtonType.SUBMIT}>
            {t('international-checkout-form-body.place-order')}
          </Button>
        )}
        <LegalCopy />
        <div className='h-6' />
      </div>
    </>
  )
}
